import * as path from "node:path";
import { checkHasProjectFile, writeProjectFile } from "@prismicio/plugin-kit/fs";
import { source } from "common-tags";
import { loadFile } from "magicast";
import { checkIsTypeScriptProject } from "../lib/checkIsTypeScriptProject.js";
import { getJSFileExtension } from "../lib/getJSFileExtension.js";
import { getSvelteMajor } from "../lib/getSvelteMajor.js";
import { rejectIfNecessary } from "../lib/rejectIfNecessary.js";
import { upsertSliceLibraryIndexFile } from "../lib/upsertSliceLibraryIndexFile.js";
import { prismicIOFileTemplate, previewAPIRouteTemplate, rootLayoutTemplate } from "./project-init.templates.js";
const installDependencies = async ({ installDependencies: installDependencies2 }) => {
  await installDependencies2({
    dependencies: {
      "@prismicio/client": "latest",
      "@prismicio/svelte": "latest"
    }
  });
};
const createPrismicIOFile = async ({ helpers, options }) => {
  const extension = await getJSFileExtension({ helpers, options });
  const filename = path.join(`src/lib/prismicio.${extension}`);
  if (await checkHasProjectFile({ filename, helpers })) {
    return;
  }
  const typescript = await checkIsTypeScriptProject({ helpers, options });
  const contents = prismicIOFileTemplate({ typescript });
  await writeProjectFile({
    filename,
    contents,
    format: options.format,
    helpers
  });
};
const createPreviewRouteMatcherFile = async ({ helpers, options }) => {
  const extension = await getJSFileExtension({ helpers, options });
  const filename = path.join(`src/params/preview.${extension}`);
  if (await checkHasProjectFile({ filename, helpers })) {
    return;
  }
  const contents = source`
		export function match(param) {
			return param === 'preview';
		}
	`;
  await writeProjectFile({
    filename,
    contents,
    format: options.format,
    helpers
  });
};
const createPreviewAPIRoute = async ({ helpers, options }) => {
  const extension = await getJSFileExtension({ helpers, options });
  const filename = path.join("src", "routes", "api", "preview", `+server.${extension}`);
  if (await checkHasProjectFile({ filename, helpers })) {
    return;
  }
  const typescript = await checkIsTypeScriptProject({ helpers, options });
  const contents = previewAPIRouteTemplate({ typescript });
  await writeProjectFile({
    filename,
    contents,
    format: options.format,
    helpers
  });
};
const createPreviewRouteDirectory = async ({ helpers, options }) => {
  const filename = path.join("src", "routes", "[[preview=preview]]", "README.md");
  if (await checkHasProjectFile({ filename, helpers })) {
    return;
  }
  const contents = source`
		This directory adds support for optional \`/preview\` routes. Do not remove this directory.

		All routes within this directory will be served using the following URLs:

		- \`/example-route\` (prerendered)
		- \`/preview/example-route\` (server-rendered)

		See <https://prismic.io/docs/svelte-preview> for more information.
	`;
  await writeProjectFile({
    filename,
    contents,
    format: options.format,
    helpers
  });
};
const createRootLayoutServerFile = async ({ helpers, options }) => {
  const extension = await getJSFileExtension({ helpers, options });
  const filename = path.join(`src/routes/+layout.server.${extension}`);
  if (await checkHasProjectFile({ filename, helpers })) {
    return;
  }
  const contents = source`
		export const prerender = "auto";
	`;
  await writeProjectFile({
    filename,
    contents,
    format: options.format,
    helpers
  });
};
const createRootLayoutFile = async ({ helpers, options }) => {
  const filename = path.join("src", "routes", "+layout.svelte");
  if (await checkHasProjectFile({ filename, helpers })) {
    return;
  }
  const contents = rootLayoutTemplate({ version: await getSvelteMajor() });
  await writeProjectFile({
    filename,
    contents,
    format: options.format,
    formatOptions: {
      prettier: {
        plugins: ["prettier-plugin-svelte"],
        parser: "svelte"
      }
    },
    helpers
  });
};
const modifyPrismicConfig = async ({ helpers, options, actions }) => {
  const project = await helpers.getProject();
  if (await checkHasProjectFile({
    filename: "./src/lib",
    helpers
  }) && project.config.libraries && JSON.stringify(project.config.libraries) === JSON.stringify(["./slices"])) {
    const sliceLibrary = await actions.readSliceLibrary({
      libraryID: project.config.libraries[0]
    });
    if (sliceLibrary.sliceIDs.length < 1) {
      project.config.libraries = ["./src/lib/slices"];
    }
  }
  await helpers.updatePrismicConfig(project.config, {
    format: options.format
  });
};
const upsertSliceLibraryIndexFiles = async (context) => {
  const project = await context.helpers.getProject();
  if (!project.config.libraries) {
    return;
  }
  await Promise.all(project.config.libraries.map(async (libraryID) => {
    await upsertSliceLibraryIndexFile({ libraryID, ...context });
  }));
};
const modifyViteConfig = async ({ helpers, options }) => {
  let filename = "vite.config.js";
  if (!await checkHasProjectFile({ filename, helpers })) {
    filename = "vite.config.ts";
  }
  if (!await checkHasProjectFile({ filename, helpers })) {
    return;
  }
  const filepath = helpers.joinPathFromRoot(filename);
  const mod = await loadFile(filepath);
  if (mod.exports.default.$type !== "function-call") {
    return;
  }
  const config = mod.exports.default.$args[0];
  config.server ??= {};
  config.server.fs ??= {};
  config.server.fs.allow ??= [];
  if (!config.server.fs.allow.includes("./prismic.config.json")) {
    config.server.fs.allow.push("./prismic.config.json");
  }
  const contents = mod.generate().code.replace(/\n\s*\n(?=\s*server:)/, "\n");
  await writeProjectFile({
    filename,
    contents,
    format: options.format,
    helpers
  });
};
const projectInit = async ({ installDependencies: _installDependencies }, context) => {
  rejectIfNecessary(await Promise.allSettled([
    installDependencies({ installDependencies: _installDependencies }),
    modifyPrismicConfig(context),
    createPrismicIOFile(context),
    createPreviewAPIRoute(context),
    createPreviewRouteDirectory(context),
    createPreviewRouteMatcherFile(context),
    createRootLayoutServerFile(context),
    createRootLayoutFile(context),
    modifyViteConfig(context)
  ]));
  await upsertSliceLibraryIndexFiles(context);
};
export {
  projectInit
};
//# sourceMappingURL=project-init.js.map
